import type { ProblemDetail } from '@bm/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetail,
  ) {
    // Field-level validation messages (from ZodValidationPipe's `errors`) are
    // more useful to show than the generic "Bitte die markierten Felder
    // prüfen." detail alone — fold them in so a form's error banner names
    // the actual problem instead of leaving the user guessing.
    const fieldErrors = problem.errors
      ? Object.values(problem.errors).flat().join(' ')
      : undefined;
    super(fieldErrors || problem.detail || problem.title || `HTTP ${status}`);
  }
}

function csrfToken(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)bm_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : undefined;
}

let activeOrgId: string | null = null;
export function setActiveOrg(id: string | null): void {
  activeOrgId = id;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  orgScoped?: boolean;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }
  if (opts.orgScoped && activeOrgId) headers['X-Organization-Id'] = activeOrgId;

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    throw new ApiError(res.status, (data as ProblemDetail) ?? { title: 'Error', status: res.status, type: 'about:blank' });
  }
  return data as T;
}
